import { Component, inject, OnDestroy, HostListener } from '@angular/core';
import { ActivatedRoute, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatTableDataSource, MatTableModule } from '@angular/material/table'
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon'
import { MatExpansionModule } from '@angular/material/expansion'
import { FormControl, FormGroup, ReactiveFormsModule, FormBuilder } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox'
import { MatMenuModule } from '@angular/material/menu'
import { MatInputModule } from '@angular/material/input'
import { MatFormField } from '@angular/material/input';
import { MatDividerModule } from '@angular/material/divider'
import { MatChipsModule } from '@angular/material/chips'
import { AsyncPipe, CommonModule, DatePipe, TitleCasePipe } from '@angular/common';
import { BehaviorSubject, map, Observable, startWith, take, Subscription, skip } from 'rxjs';
import { ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { ObservationsSearchService } from './observations-search.service';
import { TableFilteringService } from '../shared/table-filtering.service';
import { AgePipe } from '../age.pipe';
import { TableHeaderComponent } from '../table-header/table-header.component';
import { ObservationValueDisplayComponent } from '../shared/observation-value-display/observation-value-display.component';
import { HttpClient } from '@angular/common/http';
import { PatientDetailsKeyService } from '../patient-sidedetails/patient-details-key.service';
import { EncounterServiceService } from '../patient-wrapper/encounter-service.service';
import { baseStatusStyles } from '../shared/statusUIIcons';
import { StateService } from '../shared/state.service';
import { CodeableConcept2Pipe } from '../shared/codeable-concept2.pipe';
import { NaPipe } from '../shared/na.pipe';
import { JoinedCodeableConceptPipe } from "../shared/joinedcodeableconcept.pipe";
import { join } from 'path';
import { ValueToStringPipe } from '../shared/value-to-string.pipe';
import { ChipsDirective } from '../chips.directive';
import { CodeableRef2Pipe } from '../shared/codeable-ref2.pipe';
import { UtilityService } from '../shared/utility.service';
import { ReferenceDisplayDirective } from '../shared/reference-display.directive';
import { TruncateWordsDirective } from '../shared/truncate-words.directive';
import { EmptyStateComponent } from '../shared/empty-state/empty-state.component';
import { AuthService } from '../shared/auth/auth.service';
import { MatDialog } from '@angular/material/dialog';
import { ObservationsFilterDialogComponent } from './observations-filter-dialog.component';
import { RowDetailsDialogComponent } from './add-observation/add-observation.component';
import { DetailsBuilderObject } from '../detailz-viewz/details-builder.service';
import { DetailzViewzComponent } from '../detailz-viewz/detailz-viewz.component';
import { CustomTabsComponent, TabItem } from '../shared/custom-tabs/custom-tabs.component';
import { ResourceStatusChangesService } from '../resource-status-changes/resource-status-changes.service';
import { DynamicFormsV2Component } from '../shared/dynamic-forms-v2/dynamic-forms-v2.component';
import { SingleCodeField, formMetaData, generalFieldsData } from '../shared/dynamic-forms.interface2';
import { ErrorService } from '../shared/error.service';
import { Observation } from 'fhir/r4';
import { ObservationReferenceRangeSuggestionDialogComponent, ReferenceRangeSuggestionDialogResult } from '../observation-reference-ranges/reference-range-suggestion-dialog/reference-range-suggestion-dialog.component';
import { SuccessToastService } from '../shared/success-toast.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ObservationReferenceRangeMatcherService } from '../shared/observation-reference-range-matcher.service';
import { OrganizationSettingsService } from '../shared/organization-settings.service';

@Component({
  selector: 'app-patient-observation',
  standalone: true,
  imports: [MatCardModule, MatButtonModule, CodeableConcept2Pipe, NaPipe,
    MatProgressSpinnerModule,
    JoinedCodeableConceptPipe, ValueToStringPipe, ChipsDirective,
    EmptyStateComponent, ObservationValueDisplayComponent,
    MatDividerModule, DatePipe, CommonModule,
    RouterLinkActive, ReferenceDisplayDirective,
    MatExpansionModule, MatCheckboxModule, TitleCasePipe,
    MatTableModule, AsyncPipe,
    MatChipsModule,
    MatPaginatorModule,
    MatInputModule,
    MatMenuModule, TableHeaderComponent,
    MatTableModule, MatIconModule, ReactiveFormsModule, JoinedCodeableConceptPipe,
    CustomTabsComponent],
  templateUrl: './patient-observation.component.html',
  styleUrl: './patient-observation.component.scss'
})
export class PatientObservationComponent {
  utilityService = inject(UtilityService);
  route = inject(ActivatedRoute);
  patientObservationData: any;
  tableDataSource = new MatTableDataSource();
  tableDataLevel2: BehaviorSubject<any> = new BehaviorSubject([])
  references: Map<string, any> = new Map();
  categoryFiltering = new FormControl('vital-signs');
  categoryTabs: TabItem[] = [
    { id: 'vital-signs', label: 'Vital Signs', title: 'Vital Signs' },
    { id: 'exam', label: 'Physical Examination', title: 'Physical Examination' },
    { id: 'patient-symptoms', label: 'Patient Symptoms', title: 'Patient Symptoms' },
    //lab - 
    { id: 'laboratory', label: 'Lab', title: 'Lab' },
    //social history -
    { id: 'social-history', label: 'Social Hist.', title: 'Social Hist.' },
    { id: 'others', label: 'Others', title: 'Others' }
  ];

  private normalizeCategoryValue(value: unknown): string {
    return (value ?? '')
      .toString()
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '-');
  }

  private getObservationCategoryCodes({ rawResource: observation }: { rawResource: Observation }): string[] {

    const categories = Array.isArray(observation?.category) ? observation.category : [];
    const fromCodings = categories
      .flatMap((cat: any) => Array.isArray(cat?.coding) ? cat.coding : [])
      .map((coding: any) => this.normalizeCategoryValue(coding?.code))
      .filter(Boolean);

    const fromTexts = categories
      .map((cat: any) => this.normalizeCategoryValue(cat?.text))
      .filter(Boolean);


    return Array.from(new Set([...fromCodings, ...fromTexts]));
  }
  private readonly ALL_OBSERVATION_CATEGORIES = [
    'vital-signs',
    'physical-exam',
    'exam',
    'imaging',
    'laboratory',
    'procedure',
    'survey',
    'social-history',
    'nutrition',
    'family-history',
    'device',
    'others'
  ];
  statuStyles = baseStatusStyles
  patientObservationTableFilter: Map<string, any[]> = new Map([[
    // TODO: Add more filters
    'status', ['registered',
      'preliminary',
      'final',
      'amended',
      'corrected',
      'cancelled',
      'entered-in-error',
      'unknown']
  ]]);
  observationSearchFieldOptions: any[] = [];

  patientObservationTableFilterArray = this.patientObservationTableFilter;
  patientObservationFiltersFormControlObject: any = {};
  encounterService = inject(EncounterServiceService);
  allowedObservationCategories: string[] = [];
  filteredCategoryButtons: string[] = [];
  patientName!: Observable<string>;
  patientId!: string;
  patientOpenAndClose = inject(PatientDetailsKeyService);
  patientObservationDisplayedColumns = ['dateTaken', 'category', 'name', 'status',
    'result', 'practitioner', 'action'
  ];
  // Responsive column sets (desktop vs mobile)
  private desktopColumns: string[] = ['dateTaken', 'category', 'name', 'status', 'result', 'practitioner', 'action'];
  private mobileColumns: string[] = ['dateTaken', 'name', 'result', 'action'];
  private subToService: Subscription | null = null;
  private categorySub: Subscription | null = null;
  private stateSub: Subscription | null = null;
  @ViewChild(MatPaginator) paginator?: MatPaginator;
  private dialog = inject(MatDialog);
  readonly obsSearch = inject(ObservationsSearchService);
  private tableFiltering = inject(TableFilteringService) as unknown as TableFilteringService<any, any>;
  private stateService = inject(StateService);
  total$ = this.obsSearch.total$;
  loading$ = this.obsSearch.loading$;
  // ['basedOn', 'category', 'code', 'subject', 'performer', 'referenceRange', 'value[x]', 'status', 'effective[x]', 'component', 'issued'];
  // ['category', 'code', 'value', 'unit', 'status'];
  http = inject(HttpClient);
  // Inject auth and track role
  authService = inject(AuthService);
  userRole: string | null = null;
  private readonly resourceStatusChanges = inject(ResourceStatusChangesService);
  private readonly errorService = inject(ErrorService);
  private readonly successToast = inject(SuccessToastService);
  private fb = inject(FormBuilder);
  private observationReferenceRangeMatcher = inject(ObservationReferenceRangeMatcherService);
  private organizationSettings = inject(OrganizationSettingsService);

  canManageManualReferenceRanges$ = this.organizationSettings.canManageManualReferenceRanges();

  observationDetailsBuilder: DetailsBuilderObject = {
    resourceName: 'Observation',
    resourceIcon: 'monitor_heart',
    specialHeader: {
      strongSectionKey: 'codeAndValue',
      iconSectionKeys: ['status'],
      contentSectionKeys: ['subject', 'effectiveDateIso']
    },
    groups: [
      { groupName: 'Context', groupIcon: 'info', groupKeys: ['status', 'category', 'encounter', 'effective Date'] },
      { groupName: 'Result', groupIcon: 'analytics', groupKeys: ['displayValue', 'interpretation', 'referenceRange'] },
      { groupName: 'Participants', groupIcon: 'group', groupKeys: ['performer', 'subject'] },
      { groupName: 'Notes', groupIcon: 'notes', groupKeys: ['note'] }
    ],
    otherKeys: ['displayValue', 'codeAndValue']
  };
  selectedSearchField = "";
  ngOnInit() {
    this.patientId = this.route.parent?.snapshot.params['id'] || '';
    console.log(this.patientId);
    const allCategories = Array.from(new Set([
      ...this.ALL_OBSERVATION_CATEGORIES,
      'patient-symptoms'
    ]));
    this.filteredCategoryButtons = this.authService.getFilteredObservationCategories(allCategories);
    this.allowedObservationCategories = this.authService.getAllowedObservationCategories();

    // this.patientName = this.http.get("sample_fhir_patients.json").pipe(map((allArray: any) => {
    //   return allArray.find((element: any) => {
    //     return element.identifier[0].value === this.route.parent?.snapshot.params['id']
    //   })
    // }), map((patient: any) => {
    //   return patient.name[0].given.join(' ') + ' ' + patient.name[0].family;
    // }));

    console.log(this.patientName);
    // if there is a patientId observationSearchFieldOptionswould not include 'subject'
    // // format  { label: 'Identifiers', value: 'identifier' },
    // { label: 'Name', value: 'name' }
    //  since it's not a useful search criteria in that context; if there is no patientId, we want to allow searching by subject (which could be patient or group) to find relevant observations
    this.observationSearchFieldOptions = this.patientId ? [{
      label: 'Name', value: 'code:text'
    }] : [
      {
        label: 'Name', value: 'code'
      }, {
        label: 'Patient Id', value: 'subject'
      }
    ];
    this.selectedSearchField = this.observationSearchFieldOptions[0].value;
    for (const [key, value] of this.patientObservationTableFilter) {
      this.patientObservationFiltersFormControlObject[key] = new FormGroup({});
      for (const filterValue of value) {
        this.patientObservationFiltersFormControlObject[key].addControl(filterValue, new FormControl(false));
      }
    }
    // (switched to server-driven paging below)
    // Initialize observations search service and wire its datasource
    this.obsSearch.init(this.patientId, { pageSize: 10 });
    // setup responsive columns and state subscriptions
    this.updateDisplayedColumns();
    this.subscribeToStateChanges();
    // this.tableDataSource = this.obsSearch.dataSource as any;

    // Subscribe to the upstream paged data source BEFORE we override connect(),
    // so we don't accidentally subscribe to the subject we write back to (avoids infinite loop).
    const upstream$ = (this.obsSearch.dataSource.connect() as any);
    this.subToService = upstream$.subscribe((rows: any[]) => {
      console.log(rows)
      const currentTab = this.normalizeCategoryValue(this.categoryFiltering.value ?? 'vital-signs');
      const filtered = (rows || []).filter((obs: any) => this.postFilterByCategory(obs, currentTab));
      // push a new array to the table subject
      this.tableDataLevel2.next(Array.isArray(filtered) ? filtered.slice() : []);
    });

    // Provide the table with our post-filtered subject as its connect() source
    this.tableDataSource.connect = () => {
      console.log(this.tableDataLevel2)
      return this.tableDataLevel2;
    }

    // When category changes, ask server to filter first; server-driven mapping below
    this.categorySub = this.categoryFiltering.valueChanges.pipe(startWith('vital-signs')).subscribe((value) => {
      const tab = this.normalizeCategoryValue(value);
      const serverFilter: any = {};
      // Map simple tabs to server category where possible
      if (tab === 'others') {
        serverFilter['category:not'] = 'vital-signs,exam,patient-symptoms,laboratory,social-history';
      } else if (tab) {

        // For 'exam' / 'physical-exam' prefer passing 'exam' (server may or may not support nuanced semantics)
        serverFilter['category'] = tab === 'physical-exam' ? 'exam' : tab;
        if (tab === 'physical-exam' || tab === 'exam') {
          serverFilter['category:not'] = 'patient-symptoms';
        }
      }
      // Trigger server search; we'll post-filter the returned page for edge cases
      // reset paginator UI and ensure service is on page 0
      try {
        this.paginator?.firstPage();
      } catch (e) {
        // ignore
      }
      this.obsSearch.changePage(0);
      this.obsSearch.search(serverFilter);
    });
  }

  @HostListener('window:resize', ['$event'])
  onWindowResize(): void {
    this.updateDisplayedColumns();
  }

  private updateDisplayedColumns(): void {
    try {
      const isMobile = window.innerWidth < 600;
      this.patientObservationDisplayedColumns = isMobile ? [...this.mobileColumns] : [...this.desktopColumns];
    } catch (e) {
      // fallback to desktop
      this.patientObservationDisplayedColumns = [...this.desktopColumns];
    }
  }

  private subscribeToStateChanges(): void {
    // React to persisted patient observation changes in StateService and refresh current page.
    // skip(1) prevents reacting to the initial empty/seed value.
    try {
      this.stateSub = this.stateService.PatientResources.observations.pipe(skip(1)).subscribe(() => {
        try { this.obsSearch.refresh(true); } catch (e) { }
      });
    } catch (e) {
      // ignore if stateService not available
    }
  }

  ngOnDestroy(): void {
    try { this.subToService?.unsubscribe(); } catch (e) { }
    try { this.categorySub?.unsubscribe(); } catch (e) { }
    try { this.stateSub?.unsubscribe(); } catch (e) { }
    try { this.obsSearch.destroy(); } catch (e) { }
  }

  onHeaderFilterChanged(event: { filterKey: string; values: string[] }): void {
    const tab = this.normalizeCategoryValue(this.categoryFiltering.value ?? 'vital-signs');
    const payload: any = {};
    //join arrays with ","
    if (event?.filterKey) payload[event.filterKey] = event.values.join(',');
    if (tab === 'others') {
      payload['category:not'] = 'vital-signs,exam,patient-symptoms,laboratory,social-history';
    } else if (tab) {
      payload['category'] = tab === 'physical-exam' ? 'exam' : tab;
      if (tab === 'physical-exam' || tab === 'exam') payload['category:not'] = 'patient-symptoms';
    }
    try { this.paginator?.firstPage(); } catch (e) { }
    this.obsSearch.changePage(0);
    this.obsSearch.search(payload);
  }

  onHeaderSearchCriteriaChanged(criteria: { term: string; field: string | null }): void {
    const tab = this.normalizeCategoryValue(this.categoryFiltering.value ?? 'vital-signs');
    const payload: any = {};
    const term = (criteria?.term ?? '').trim();
    const field = criteria?.field ?? 'code:text';
    if (term) {
      payload[field] = term;
    }
    if (tab === 'others') {
      payload['category:not'] = 'vital-signs,exam,patient-symptoms,laboratory,social-history';
    } else if (tab) {
      payload['category'] = tab === 'physical-exam' ? 'exam' : tab;
      if (tab === 'physical-exam' || tab === 'exam') payload['category:not'] = 'patient-symptoms';
    }
    try { this.paginator?.firstPage(); } catch (e) { }
    this.obsSearch.changePage(0);
    this.obsSearch.search(payload);
  }

  openAddObservationDialog() {
    const dialogRef = this.encounterService.addAnyObservation(this.patientId);
    if (dialogRef) {
      dialogRef.afterClosed().pipe(take(1)).subscribe((result) => {
        // console.log('add obs is closing', result);
        // debugger;
        if (result) {
          try { this.obsSearch.refresh(true); } catch (e) { }
        }
      });
    }
  }

  openFiltersDialog(): void {
    const currentTab = this.normalizeCategoryValue(this.categoryFiltering.value ?? 'vital-signs');
    const ref = this.dialog.open(ObservationsFilterDialogComponent, { data: { currentTab } });
    ref.afterClosed().pipe(take(1)).subscribe((result: any) => {
      if (!result) return;
      // Merge with existing filters so advanced filters accumulate until cleared
      this.tableFiltering.filters$.pipe(take(1)).subscribe((currentFilters) => {
        const merged: any = { ...currentFilters };
        for (const [k, v] of Object.entries(result || {})) {
          if (Array.isArray(v)) {
            const existing = Array.isArray(merged[k]) ? merged[k] as any[] : (merged[k] ? [merged[k]] : []);
            merged[k] = Array.from(new Set([...existing, ...v]));
          } else {
            // scalar override
            merged[k] = v;
          }
        }
        if (currentTab === 'others') {
          merged['category:not'] = 'vital-signs,exam,patient-symptoms,laboratory,social-history';
        } else if (currentTab) {
          merged['category'] = currentTab === 'physical-exam' ? 'exam' : currentTab;
          if (currentTab === 'physical-exam' || currentTab === 'exam') merged['category:not'] = 'patient-symptoms';
        }
        try { this.paginator?.firstPage(); } catch (e) { }
        this.obsSearch.changePage(0);
        this.obsSearch.search(merged);
      });
    });
  }

  onObsPageChange(event: PageEvent): void {
    this.obsSearch.changePage(event.pageIndex);
  }

  private prepareObservationForDetails(observation: any): any {
    if (!observation) return observation;
    const displayValue = this.computeObservationDisplayValue(observation);
    const codeLabel =
      observation?.code?.text ||
      observation?.code?.coding?.[0]?.display ||
      observation?.code?.coding?.[0]?.code ||
      '';

    const codeAndValue = [codeLabel, displayValue].filter(Boolean).join(' — ');

    return {
      ...observation,
      displayValue,
      codeAndValue
    };
  }

  private postFilterByCategory(obs: any, filterValue: string): boolean {
    if (filterValue === 'others') {
      return true;
    }

    if (!this.canAccessObservationCategory(obs)) return false;
    const categoryCodes = this.getObservationCategoryCodes(obs);
    if (categoryCodes.length === 0) return false;

    if ((filterValue === 'exam' || filterValue === 'physical-exam' || filterValue === 'social-history') &&
      categoryCodes.includes('patient-symptoms')) {
      return false;
    }

    if (filterValue === 'physical-exam') {
      return categoryCodes.includes('physical-exam') || categoryCodes.includes('exam');
    }

    return categoryCodes.includes(filterValue);
  }

  trackById(_: number, item: any) {
    return item?.id || null;
  }

  showDetails(observation: any) {
    const isPatientUser = (this.userRole ?? '').toString().toLowerCase() === 'patient';
    const canChangeStatus = !isPatientUser && this.authService.can('observation', 'update');
    const actionButtons = [];

    if (canChangeStatus) {
      actionButtons.push({
        label: 'Change Status',
        icon: 'edit',
        key: 'change_status'
      });
    }

    const detailsRef = this.dialog.open(DetailzViewzComponent, {
      maxHeight: '93vh',
      maxWidth: '90vh',
      data: {
        resourceData: { ...observation, ...observation.rawResource },
        detailsBuilderObject: this.observationDetailsBuilder,
        actionButtons: actionButtons
      }
    });

    detailsRef.componentInstance.actionInvoked?.subscribe(({ key, resource }: any) => {
      if (key === 'change_status') {
        this.openObservationStatusChangeDialog(resource);
      }
    });
  }

  openReferenceRangeDialog(observation: any): void {
    if (!observation?.id) {
      this.errorService.openandCloseError('Save the observation before adding reference ranges.');
      return;
    }
    const dialogRef = this.dialog.open(ObservationReferenceRangeSuggestionDialogComponent, {
      width: '820px',
      maxHeight: '92vh',
      data: {
        observation: observation.rawResource || observation,
        patient: this.patientId
      }
    });

    dialogRef.afterClosed().subscribe((result?: ReferenceRangeSuggestionDialogResult) => {
      const updated = (result as any)?.updatedObservation;
      if (updated) {
        this.successToast.success('Reference range applied.');
        try { this.obsSearch.refresh(); } catch (e) { }
      }
    });
  }


  private openObservationStatusChangeDialog(observation: any): void {
    const isPatientUser = (this.userRole ?? '').toString().toLowerCase() === 'patient';
    if (isPatientUser || !this.authService.can('observation', 'update')) {
      this.errorService.openandCloseError('You do not have permission to edit observations.');
      return;
    }

    const observationId = observation?.id || '';
    if (!observationId) {
      this.errorService.openandCloseError('Observation id is missing; status cannot be changed.');
      return;
    }

    const proceed = () => {
      const currentStatus = observation?.status || '';
      const availableStatuses = this.resourceStatusChanges.getStatusCodes('Observation') || [
        'registered',
        'preliminary',
        'final',
        'amended',
        'corrected',
        'cancelled',
        'entered-in-error',
        'unknown'
      ];

      const dynF = this.dialog.open(DynamicFormsV2Component, {
        width: '420px',
        data: {
          formMetaData: <formMetaData>{
            formName: 'Change Observation Status',
            closeDialogOnSubmit: true,
            formDescription: currentStatus ? `Current status: ${currentStatus}` : 'Select a new status',
            submitText: 'Update Status',
          },
          formFields: [
            <SingleCodeField>{
              generalProperties: <generalFieldsData>{
                fieldApiName: 'status',
                fieldLabel: 'Status',
                fieldName: 'Status',
                fieldType: 'SingleCodeField',
                inputType: 'select',
                isArray: false,
                isGroup: false,
                value: currentStatus,
                validations: [{ type: 'default', isFunction: false, name: 'required' }],
                auth: { read: 'all', write: 'doctor, nurse, admin' }
              },
              data: availableStatuses,
              dataType: 'string'
            }
          ]
        }
      });

      dynF.afterClosed().subscribe((formResult) => {
        const newStatus = formResult?.values?.status;
        if (!newStatus || newStatus === currentStatus) return;
        this.resourceStatusChanges
          .changeResourceStatus('Observation', observationId, newStatus, 'auto')
          .subscribe();
      });
    };

    const encounterRef = observation.rawResource?.encounter?.reference || observation.encounter?.reference;
    if (encounterRef) {
      const encId = encounterRef.split('/')[encounterRef.split('/').length - 1] || encounterRef;
      const backendUrl = this.encounterService.backendEndPoint;
      this.http.get<any>(`${backendUrl}/Encounter/${encId}`).subscribe({
        next: (enc) => {
          if (enc && ['finished', 'cancelled', 'completed', 'unknown'].includes(enc.status)) {
            this.errorService.openandCloseError('Cannot change status: The associated encounter is closed.');
          } else {
            proceed();
          }
        },
        error: () => proceed()
      });
    } else {
      proceed();
    }
  }



  private canAccessObservationCategory(observation: any): boolean {
    const categoryCodes = this.getObservationCategoryCodes(observation);
    if (categoryCodes.length === 0) {
      console.warn('Unable to extract category from observation', observation);
      return false;
    }

    // Multi-category observations (like patient symptoms) should be visible if the user can view ANY of its categories.
    return categoryCodes.some((c) => this.authService.can('observation', 'view', c));
  }
  private computeEmptyValueKeys(observation: any): string[] {
    if (!observation) return [];
    return Object.keys(observation)
      .filter(key => key.startsWith('value') && this.isEmptyValue(observation[key]));
  }

  private isEmptyValue(value: any): boolean {
    if (value == null) return true;
    if (typeof value === 'string') return value.trim() === '';
    if (Array.isArray(value)) return value.length === 0 || value.every(v => this.isEmptyValue(v));
    if (typeof value === 'object') return Object.keys(value).length === 0;
    return false;
  }

  private isBloodPressure(obs: any): boolean {
    const codeable = obs?.code;
    const codeStr = (codeable?.text || '').toLowerCase();
    const coding = Array.isArray(codeable?.coding) ? codeable.coding : [];
    const hasPanelCode = coding.some((c: any) => c?.code === '85354-9');
    const mentionsBp = codeStr.includes('blood pressure') || coding.some((c: any) => (c?.display || '').toLowerCase().includes('blood pressure'));
    const components = Array.isArray(obs?.component) ? obs.component : [];
    const hasSys = components.some((c: any) => c?.code?.coding?.some((k: any) => k?.code === '8480-6'));
    const hasDia = components.some((c: any) => c?.code?.coding?.some((k: any) => k?.code === '8462-4'));
    return hasPanelCode || mentionsBp || (hasSys && hasDia);
  }

  private bpDisplayFromObs(obs: any): string {
    const components = Array.isArray(obs?.component) ? obs.component : [];
    const getVal = (code: string) => {
      const comp = components.find((c: any) => c?.code?.coding?.some((k: any) => k?.code === code));
      const v = comp?.valueQuantity?.value;
      const unit = comp?.valueQuantity?.unit || comp?.valueQuantity?.code || '';
      return { v, unit };
    };
    const sys = getVal('8480-6'); // Systolic
    const dia = getVal('8462-4'); // Diastolic
    if (sys?.v != null && dia?.v != null) {
      // Normalize mm[Hg] -> mmHg
      const unit = (sys.unit || dia.unit || '').replace('mm[Hg]', 'mmHg') || 'mmHg';
      return `${sys.v}/${dia.v} ${unit}`.trim();
    }
    return '';
  }

  private computeObservationDisplayValue(obs: any): string {
    if (this.isBloodPressure(obs)) {
      const bp = this.bpDisplayFromObs(obs);
      if (bp) return bp;
    }
    // fallback to generic stringifier (covers valueQuantity, valueCodeableConcept, etc.)
    return (new ValueToStringPipe(this.utilityService)).transform(obs) || '';
  }

  isCategoryVisible(categoryCode: string): boolean {
    return this.filteredCategoryButtons.includes(categoryCode);
  }


  getPermissionInfo(): { allowed: string[]; role: string; } {
    return {
      allowed: this.allowedObservationCategories,
      role: this.userRole || 'unknown'
    };
  }
  // Permission helpers (aligns with capacityObject.observation)
  get canAddObservation(): boolean {
    const isPatientUser = (this.userRole ?? '').toString().toLowerCase() === 'patient';
    return !isPatientUser && this.authService.can('observation', 'add');
  }
  get canExportObservation(): boolean {
    return this.authService.can('observation', 'viewAll');
  }

  get canViewAllObservations(): boolean {
    return this.authService.can('observation', 'viewAll');
  }
}
